import React, { useState, useEffect } from 'react';
import { Menu, SquareX, ArrowRight } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

type UserData = {
  email: string;
  is_user_login: boolean;
  login_username: string;
  name: string;
  picture: string;
  redirect_url: string;
  user_id: number;
  user_type: number;
  admin_id?: number;
};

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [userData, setUserData] = useState<UserData | null>(null);
  const navigate = useNavigate();

  const menuItems = [
    { name: 'Home', to: '/' },
    { name: 'About Us', to: '/about-us' },
    { name: 'How It Works', to: '/how-it-works' },
    { name: 'Blog', to: '/blog' },
    { name: 'Contact Us', to: '/contact-us' },
    // { name: 'Pricing', to: '/pricing' },
  ];

  // Initial load from localStorage and listen for logout event
  useEffect(() => {
    const loadUserData = () => {
      const storedUser = localStorage.getItem('userData');
      if (storedUser) {
        try {
          setUserData(JSON.parse(storedUser));
        } catch {
          setUserData(null);
        }
      } else {
        setUserData(null);
      }
    };

    loadUserData();

    const syncLogout = (event: StorageEvent) => {
      if (event.key === 'logout' || (event.key === 'userData' && event.newValue === null)) {
        setUserData(null);
      }
    };

    window.addEventListener('storage', syncLogout);

    return () => {
      window.removeEventListener('storage', syncLogout);
    };
  }, []);

  // Logout handler
  const handleLogout = () => {
    localStorage.removeItem('userData');
    localStorage.setItem('logout', Date.now().toString());
    setUserData(null);
    setMenuOpen(false);
    navigate('/login');
  };

  return (
    <>
      <header className="bg-white text-white flex justify-between shadow-none border-b-none">
        <div className="flex">
          <div className="py-4 pr-4 lg:pl-[36px] pl-[30px]">
            <Link to="/">
              <img src="/images/LeveQR-logo-06.png" alt="LiveQR Logo" className="w-[200px] h-auto" />
            </Link>
          </div>
        </div>

        {/* Desktop Login/Signup */}
        <div className="flex">
          <div className="gap-4 hidden sm:flex items-center font-normal">
            {(userData?.user_id || userData?.admin_id) ? (
              <>
                <button
                  className="relative text-black leading-tight transform scale-y-125 after:content-[''] after:absolute after:w-full after:h-0.5 after:rounded after:bg-[#F47321] hover:text-[#F47321] after:bottom-[-2px] after:left-0 after:origin-right after:scale-x-0 after:transition-transform after:duration-300 after:ease-in-out hover:after:origin-left hover:after:scale-x-100 p-[8px]"
                  onClick={handleLogout}
                >
                  Log Out
                </button>
                <button
                  className="bg-[#F47321] text-white rounded-none px-4 py-2 text-xs sm:px-3 sm:py-1 sm:text-sm md:px-8 md:py-2 md:text-base btn-hover-effect flex items-center gap-2 font-normal leading-[1.2] transition"
                  onClick={() => {
                    setMenuOpen(false);
                    if (userData?.redirect_url) {
                      window.location.href = userData.redirect_url;
                    } else {
                      navigate('/dashboard');
                    }
                  }}
                >
                  <span>Go to Dashboard</span>
                  <span className="icon-arrow-right">
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </span>
                </button>
                <div
                  className="hidden md:flex items-center justify-center w-20 h-full bg-[#156E77] cursor-pointer"
                  onClick={() => setMenuOpen(true)}
                >
                  <Menu className="w-6 h-6 text-white" />
                </div>
              </>
            ) : (
              <>
                <button
                  className="relative text-black leading-tight transform scale-y-125 after:content-[''] after:absolute after:w-full after:h-0.5 after:rounded after:bg-[#F47321] hover:text-[#F47321] after:bottom-[-2px] after:left-0 after:origin-right after:scale-x-0 after:transition-transform after:duration-300 after:ease-in-out hover:after:origin-left hover:after:scale-x-100 p-[8px]"
                  onClick={() => {
                    setMenuOpen(false);
                    navigate('/login');
                  }}
                >
                  Log In
                </button>
                <button
                  className="bg-[#F47321] hover:bg-[#F47321] text-white rounded-none px-4 py-2 text-xs sm:px-3 sm:py-1 sm:text-sm md:px-8 md:py-2 md:text-base btn-hover-effect flex items-center gap-2 font-normal leading-[1.2] transition transform scale-y-125"
                  onClick={() => {
                    setMenuOpen(false);
                    navigate('/signup');
                  }}
                >
                  <span>    Sign Up</span>
                </button>
                <div
                  className="hidden md:flex items-center justify-center w-20 h-full bg-[#156E77] cursor-pointer"
                  onClick={() => setMenuOpen(true)}
                >
                  <Menu className="w-6 h-6 text-white" />
                </div>
              </>
            )}
          </div>

          {/* Mobile menu icon */}
          <div
            className="md:hidden bg-[#156E77] w-20 h-20 flex items-center justify-center cursor-pointer"
            onClick={() => setMenuOpen(true)}
          >
            <Menu className="w-6 h-6 text-white" />
          </div>
        </div>
      </header>

      {/* Mobile Overlay Menu */}
      {menuOpen && (
        <div className="fixed inset-y-0 right-0 z-50 flex flex-col w-full lg:w-[50%] bg-white/70 backdrop-blur-sm transition-all duration-300 shadow-lg">
          <div className="absolute top-5 right-5 flex items-center justify-center cursor-pointer">
            <SquareX
              className="w-[50px] h-[50px] text-[#156E77]"
              onClick={() => setMenuOpen(false)}
            />
          </div>

          <div className="flex flex-col items-center justify-center h-full space-y-10">
            <div className="flex items-center justify-center">
              <Link to="/" onClick={() => setMenuOpen(false)}>
                <img src="/images/Event-Scanner-Icon.png" alt="LiveQR Logo" className="w-[70px] h-[70px]" />
              </Link>
            </div>

            <nav className="flex flex-col gap-4 text-center space-y-10">
              {menuItems.map((item, i) => (
                <Link
                  key={i}
                  to={item.to}
                  className="text-[#156E77] Bebas-neue text-2xl"
                  onClick={() => setMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </nav>

            <div className="flex gap-4 items-center justify-center">
              {(userData?.user_id || userData?.admin_id) ? (
                <>
                  <button
                    className="relative text-black leading-tight transform scale-y-125 after:content-[''] after:absolute after:w-full after:h-0.5 after:rounded after:bg-[#F47321] hover:text-[#F47321] after:bottom-[-2px] after:left-0 after:origin-right after:scale-x-0 after:transition-transform after:duration-300 after:ease-in-out hover:after:origin-left hover:after:scale-x-100 p-[8px]"
                    onClick={handleLogout}
                  >
                    Log Out
                  </button>
                  <button
                    className="bg-[#F47321] hover:bg-[#F47321] text-white rounded-none px-4 py-2 text-sm sm:px-3 sm:py-1 sm:text-sm md:px-8 md:py-2 md:text-base flex items-center gap-2 font-normal transition transform scale-y-125 btn-hover-effect"
                    onClick={() => {
                      setMenuOpen(false);
                      if (userData?.redirect_url) {
                        window.location.href = userData.redirect_url;
                      } else {
                        navigate('/dashboard');
                      }
                    }}
                  >

                    <span>Go to Dashboard</span>
                    <span className="icon-arrow-right">
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="relative text-black leading-tight transform scale-y-125 after:content-[''] after:absolute after:w-full after:h-0.5 after:rounded after:bg-[#F47321] hover:text-[#F47321] after:bottom-[-2px] after:left-0 after:origin-right after:scale-x-0 after:transition-transform after:duration-300 after:ease-in-out hover:after:origin-left hover:after:scale-x-100 p-[8px]"
                    onClick={() => {
                      setMenuOpen(false);
                      navigate('/login');
                    }}
                  >
                    Log In
                  </button>
                  <button
                    className="bg-[#F47321] hover:bg-[#F47321] text-white rounded-none px-4 py-2 text-sm sm:px-3 sm:py-1 sm:text-sm md:px-8 md:py-2 md:text-base flex items-center gap-2 font-normal transition transform scale-y-125 btn-hover-effect"
                    onClick={() => {
                      setMenuOpen(false);
                      navigate('/signup');
                    }}
                  >
                    <span>    Sign Up</span>

                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
